<template>
    <div class="w-full h-full">
        <form @submit.prevent="submitForm" action="" class="h-full w-full flex flex-col justify-between">
            <div class="p-2 pb-6 text-justify">
                <div class="grid grid-cols-4 gap-2">
                    <div class="flex flex-col">
                        <label for="title">Title</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="text" maxlength="255" id="title" 
                              v-model="formData.title">
                    </div>
                    <div class="flex flex-col">
                        <label for="startDate">Start Date*</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="date" required id="startDate" 
                              v-model="formData.start_date">
                    </div>
                    <div class="flex flex-col">
                        <label for="endDate">End Date</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between disabled:bg-gray-100 disabled:text-gray-600" type="date" id="endDate"
                              :disabled="endDateLocked"
                              v-model="formData.end_date">
                        <span v-if="isPaused" class="mt-1 text-xs text-yellow-800">
                            ⚠ Paused engagements can't have an end date. Resume the engagement first to set one.
                        </span>
                    </div>
                    <div class="flex flex-col">
                        <label for="client">Client*</label>
                        <select id="client" v-model="formData.client_id" required
                            class="bg-white border-gray-400 rounded-md p-1 border mt-1  text-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                            <option :value=null>-------</option>
                            <option v-for="client in clientOptions" :key="client.value" :value="client.value">{{ client.label }}</option>
                        </select>
                    </div>
                    <div class="flex flex-col col-span-4">
                        <label for="invoiceInstructions">
                            Invoice Instructions
                            <span class="text-gray-500 font-normal text-sm">— flags a ⚠ Note on the engagements table for whoever generates invoices</span>
                        </label>
                        <textarea class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" id="invoiceInstructions" rows="2"
                              placeholder="e.g. Ask Luke before creating each invoice — top-up amounts vary month-to-month"
                              v-model="formData.invoice_instructions"></textarea>
                    </div>
                    <div class="flex flex-col">
                        <label for="startDate">Tail Start Date</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="date" id="startDate" 
                              v-model="formData.tail_start_date">
                    </div>
                    <div class="flex flex-col">
                        <label for="endDate">Tail End Date</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="date" id="endDate" 
                              v-model="formData.tail_end_date">
                    </div>
                    <div class="flex flex-col">
                        <label for="shouldInvoice">Should Invoice</label>
                        <select id="shouldInvoice" v-model="formData.should_invoice" 
                            class="bg-white border-gray-400 rounded-md p-1 border mt-1  text-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                            <option :value=true>True</option>
                            <option :value=false>False</option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label for="shouldInvoice">Retainer On</label>
                        <select id="shouldInvoice" v-model="formData.retainer_on" 
                            class="bg-white border-gray-400 rounded-md p-1 border mt-1  text-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                            <option :value=true>True</option>
                            <option :value=false>False</option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label for="showOnInvoiceTemplate">Show on Invoice Template</label>
                        <select id="showOnInvoiceTemplate" v-model="formData.show_on_invoice_template" 
                            class="bg-white border-gray-400 rounded-md p-1 border mt-1  text-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                            <option :value=true>True</option>
                            <option :value=false>False</option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label for="invoiceLabel">Invoice Label</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="text" maxlength="255" id="invoiceLabel" 
                              v-model="formData.invoice_label">
                    </div>
                    <!-- <div class="flex flex-col">
                        <label for="tailDuration">Tail Duration (Days)</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="number" id="tailDuration" 
                              v-model="formData.tail_duration">
                    </div> -->
                    <div class="flex flex-col">
                        <label for="invoiceDueDelta">Invoice Due Delta (Days)</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="number" id="invoiceDueDelta" 
                              v-model="formData.invoice_due_delta">
                    </div>
                    <div class="flex flex-col">
                        <label for="retainerAmount">Retainer Amount (Dollars)</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="number" id="retainerAmount" 
                              v-model="formData.retainer_amount">
                    </div>
                    <div class="flex flex-col">
                        <label for="compensationType">Compensation Type</label>
                        <select id="compensationType" v-model="formData.compensation_type" 
                            class="bg-white border-gray-400 rounded-md p-1 border mt-1  text-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                            <option :value=null>-------</option>
                            <option value="consulting">Consulting</option>
                            <option value="finder's fee">Finder's Fee</option>
                            <option value="retainer">Retainer</option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label for="paymentTerms">Payment Terms*</label>
                        <input required class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="text" maxlength="255" id="paymentTerms" 
                              v-model="formData.payment_terms">
                    </div>
                    <div class="flex flex-col">
                        <label for="paymentTerms">Billing Timeframe</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="text" maxlength="255" id="paymentTerms" 
                              v-model="formData.billing_timeframe">
                    </div>
                    <div class="flex flex-col">
                        <label for="targetInvoiceDate">Target Invoice Date</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="number" id="targetInvoiceDate" 
                              v-model="formData.target_invoice_date">
                    </div>
                    <div class="flex flex-col">
                        <label for="targetDueDate">Target Due Date</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="number" id="targetDueDate" 
                              v-model="formData.target_due_date">
                    </div>
                    <div class="flex flex-col">
                        <label for="targetDueDate">Days Required for Notice</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="number" id="targetDueDate" 
                              v-model="formData.days_required_for_notice">
                    </div>
                    <div class="flex flex-col">
                        <label for="startDate">Notice Date</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="date" id="startDate" 
                              v-model="formData.notice_date">
                    </div>
                    <div class="flex flex-col">
                        <label for="typicalPayday">Typical Payday</label>
                        <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between" type="text" maxlength="255" id="typicalPayday" 
                              v-model="formData.typical_payday">
                    </div>
                    <div v-if="engagement" class="flex flex-col">
                        <div class="mt-1 flex items-center space-x-3">
                            <div class="text-sm">
                                Status:
                                <span :class="[
                                        engagement.is_paused ? 'text-yellow-800' : 'text-gray-700',
                                        'font-semibold ml-1'
                                    ]">
                                    {{ engagement.is_paused ? 'On Hold' : 'Active' }}
                                </span>
                            </div>
                            <base-button
                                v-if="canManageEngagement"
                                class="text-xs px-2 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-dark-primary disabled:hover:shadow-none"
                                :disabled="pauseLocked"
                                @click.prevent="togglePause"
                            >{{ engagement.is_paused ? 'Click Here to Resume' : 'Click Here to Pause' }}</base-button>
                        </div>
                        <div v-if="pauseLocked" class="mt-1 text-xs text-yellow-800">
                            ⚠ Engagements with an end date can't be paused. Remove the end date first to pause.
                        </div>
                        <div v-if="engagement.is_paused && engagement.current_pause_reason"
                             class="mt-1 text-xs text-gray-600 italic">
                            Reason: {{ engagement.current_pause_reason }}
                        </div>
                    </div>
                </div>

                <div v-if="engagement && engagement.pause_events && engagement.pause_events.length > 0"
                     class="mt-4 pt-4 border-t">
                    <button type="button"
                            class="flex items-center text-sm font-semibold hover:text-accent-primary"
                            @click="pauseHistoryOpen = !pauseHistoryOpen">
                        <span class="mr-1">{{ pauseHistoryOpen ? '▼' : '▶' }}</span>
                        Pause history ({{ engagement.pause_events.length }})
                    </button>
                    <div v-if="pauseHistoryOpen" class="mt-2 space-y-2">
                        <div v-for="event in engagement.pause_events"
                             :key="event.id"
                             class="text-xs border border-gray-300 rounded p-2 bg-gray-50">
                            <div class="flex items-center justify-between">
                                <div class="font-semibold">
                                    Paused {{ formatDateTime(event.started_at) }}
                                    <span v-if="event.started_by" class="font-normal text-gray-600">
                                        by {{ userName(event.started_by) }}
                                    </span>
                                </div>
                                <span v-if="!event.ended_at" class="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">
                                    currently paused
                                </span>
                            </div>
                            <div class="mt-1 text-gray-800">{{ event.started_reason }}</div>
                            <div v-if="event.ended_at" class="mt-2 pt-2 border-t border-gray-200">
                                <div class="font-semibold">
                                    Resumed {{ formatDateTime(event.ended_at) }}
                                    <span v-if="event.ended_by" class="font-normal text-gray-600">
                                        by {{ userName(event.ended_by) }}
                                    </span>
                                </div>
                                <div class="mt-1 text-gray-800">{{ event.ended_reason }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-4 pt-4 border-t">
                    <h3 class="font-semibold text-sm mb-2">
                      Bill to (used as defaults on invoices for this engagement)
                      <span v-if="!canEditBillTo" class="ml-2 text-xs font-normal text-gray-500">
                        — read-only (accounting / dev only)
                      </span>
                    </h3>
                    <div class="grid grid-cols-2 gap-2">
                        <div class="flex flex-col">
                            <label for="billToCompanyName">Bill to — Company name</label>
                            <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between disabled:bg-gray-100 disabled:text-gray-600" type="text" maxlength="255" id="billToCompanyName"
                                  :disabled="!canEditBillTo"
                                  v-model="formData.bill_to_company_name">
                        </div>
                        <div class="flex flex-col">
                            <label for="billToPerson">Bill to — Person</label>
                            <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between disabled:bg-gray-100 disabled:text-gray-600" type="text" maxlength="255" id="billToPerson"
                                  :disabled="!canEditBillTo"
                                  v-model="formData.bill_to_person">
                        </div>
                        <div class="flex flex-col col-span-2">
                            <label for="billToAddress">Bill to — Address (multi-line)</label>
                            <textarea class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between disabled:bg-gray-100 disabled:text-gray-600" id="billToAddress" rows="3"
                                  :disabled="!canEditBillTo"
                                  v-model="formData.bill_to_address"></textarea>
                        </div>
                        <div class="flex flex-col col-span-2">
                            <label for="billToEmailAddress">Bill to — Email address</label>
                            <input class="p-1 border mt-1 flex-col border-gray-400 rounded-md justify-between disabled:bg-gray-100 disabled:text-gray-600" type="email" maxlength="255" id="billToEmailAddress"
                                  :disabled="!canEditBillTo"
                                  v-model="formData.bill_to_email_address">
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex w-ful justify-center">
                <base-button-affirmative class="mx-1">Save</base-button-affirmative>
                <base-button-negative class="mx-1" @click.prevent="cancel">Cancel</base-button-negative>
            </div>
        </form>
        <EngagementPauseModal
            v-if="engagement"
            :is-visible="pauseModalVisible"
            :engagement-id="engagement.id"
            :mode="pauseModalMode"
            @close="pauseModalVisible = false"
            @success="onPauseSuccess"
        />
    </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, reactive } from 'vue';
import { useStore } from 'vuex';
import EngagementPauseModal from './EngagementPauseModal.vue';

const store = useStore();
const userPermissions = computed(() => store.getters['auth/showUserPermissions'] || {});
const canEditBillTo = computed(() =>
  !!userPermissions.value.isDev || !!userPermissions.value.isAccountingGroup
);
const canManageEngagement = computed(() =>
  !!userPermissions.value.isDev
  || !!userPermissions.value.isManagement
  || !!userPermissions.value.isSuperuser
  || !!userPermissions.value.isAccountingGroup
);

const props = defineProps([
    'engagement',
    'clients'
])

const emit = defineEmits(['submit-form', 'cancel', 'pause-changed'])

const pauseModalVisible = ref(false)
const pauseModalMode = ref('pause')
const pauseHistoryOpen = ref(false)

function formatDateTime(value) {
    if (!value) return ''
    const d = new Date(value)
    return d.toLocaleString()
}

function userName(user) {
    if (!user) return ''
    const parts = [user.first_name, user.last_name].filter(Boolean)
    return parts.length ? parts.join(' ') : (user.username || '')
}

function onPauseSuccess(updatedEngagement) {
    pauseModalVisible.value = false
    emit('pause-changed', updatedEngagement)
}

function togglePause() {
    if (pauseLocked.value) return
    pauseModalMode.value = props.engagement.is_paused ? 'unpause' : 'pause'
    pauseModalVisible.value = true
}

const formData = reactive({
    title: props.engagement ? props.engagement.title : null,
    start_date: props.engagement ? props.engagement.start_date : null,
    end_date: props.engagement ? props.engagement.end_date : null,
    client_id: props.engagement ? props.engagement.client_id : null,
    should_invoice: props.engagement ? props.engagement.should_invoice : true,
    show_on_invoice_template: props.engagement ? props.engagement.show_on_invoice_template : true,
    invoice_label: props.engagement ? props.engagement.invoice_label : null,
    // tail_duration: props.engagement ? props.engagement.tail_duration : 0,
    invoice_due_delta: props.engagement ? props.engagement.invoice_due_delta : null,
    retainer_amount: props.engagement ? props.engagement.retainer_amount : null,
    compensation_type: props.engagement ? props.engagement.compensation_type : null,
    payment_terms: props.engagement ? props.engagement.payment_terms : null,
    billing_timeframe: props.engagement ? props.engagement.billing_timeframe : null,
    target_invoice_date: props.engagement ? props.engagement.target_invoice_date : null,
    target_due_date: props.engagement ? props.engagement.target_due_date : null,
    typical_payday: props.engagement ? props.engagement.typical_payday : null,
    tail_start_date: props.engagement ? props.engagement.tail_start_date : null,
    tail_end_date: props.engagement ? props.engagement.tail_end_date : null,
    retainer_on: props.engagement ? props.engagement.retainer_on : true,
    notice_date: props.engagement ? props.engagement.notice_date : null,
    days_required_for_notice: props.engagement ? props.engagement.days_required_for_notice : null,
    bill_to_company_name: props.engagement ? props.engagement.bill_to_company_name : null,
    bill_to_person: props.engagement ? props.engagement.bill_to_person : null,
    bill_to_address: props.engagement ? props.engagement.bill_to_address : null,
    bill_to_email_address: props.engagement ? props.engagement.bill_to_email_address : null,
    invoice_instructions: props.engagement ? props.engagement.invoice_instructions : null,
})

const clientOptions = computed(() => {
    if(props.clients) {
        return props.clients.map((client) => {
            return {value: client.id, label: client.client_name}
        })
    } else {
        return []
    }
})

const isPaused = computed(() => !!props.engagement?.is_paused)
// Leave the field editable if a paused engagement already has an end date, so it can be cleared.
const endDateLocked = computed(() => isPaused.value && !formData.end_date)
// Can't pause while an end date is set (saved or typed in the form); resuming is always allowed.
const pauseLocked = computed(() => !isPaused.value && !!(formData.end_date || props.engagement?.end_date))

function cancel(){
    emit('cancel')
}

function submitForm(){
    if (isPaused.value && formData.end_date && formData.end_date !== props.engagement.end_date) {
        alert("This engagement is paused and can't have an end date. Resume the engagement first, then set the end date.")
        return
    }

    Object.keys(formData).forEach((key) => {
        if(!formData[key]){
            formData[key] = null
        }
    })
    emit('submit-form', formData)
}

</script>